import type { FoxKifuAiAnalysis, GameReviewFrame, StoneColor } from '@wx-weiqi/contracts';

export interface ObservationWinRatePoint {
  readonly gameId: string;
  readonly moveNumber: number;
  readonly blackWinRateBps: number;
  readonly blackScoreLeadHalfPoints: number;
  readonly stonesKey: string;
  readonly visits?: number;
}

export function observationFrameKey(frame: GameReviewFrame): string {
  return JSON.stringify([frame.kind, frame.color, frame.point, frame.stones]);
}

/** One real server analysis per actual game position; research variations are never a timeline. */
export function recordObservationWinRate(
  history: readonly ObservationWinRatePoint[],
  gameId: string,
  frame: GameReviewFrame,
  analysis: FoxKifuAiAnalysis,
): readonly ObservationWinRatePoint[] {
  if (analysis.recordId !== gameId || analysis.result === null) return history;
  const point: ObservationWinRatePoint = {
    gameId,
    moveNumber: frame.moveNumber,
    blackWinRateBps: analysis.result.winRateBps,
    blackScoreLeadHalfPoints: analysis.result.scoreLeadHalfPoints,
    stonesKey: observationFrameKey(frame),
  };
  return [
    ...history.filter((item) => item.gameId === gameId && item.moveNumber !== frame.moveNumber),
    point,
  ].sort((left, right) => left.moveNumber - right.moveNumber);
}

export function currentObservationWinRates(
  history: readonly ObservationWinRatePoint[],
  gameId: string,
  frames: readonly GameReviewFrame[],
): readonly ObservationWinRatePoint[] {
  return history.filter((point) => {
    const frame = frames.find((item) => item.moveNumber === point.moveNumber);
    return (
      point.gameId === gameId &&
      frame !== undefined &&
      observationFrameKey(frame) === point.stonesKey
    );
  });
}

export interface ObservationResearchBase {
  readonly gameId: string;
  readonly frame: GameReviewFrame;
  readonly prefixKey: string;
  readonly nextColor: StoneColor;
}

export function pinObservationResearchBase(
  gameId: string,
  frames: readonly GameReviewFrame[],
  cursor: number,
  currentPlayer: StoneColor,
): ObservationResearchBase | null {
  const frame = frames[cursor];
  if (frame === undefined) return null;
  return {
    gameId,
    frame,
    prefixKey: JSON.stringify(frames.slice(0, cursor + 1).map(observationFrameKey)),
    nextColor: frames[cursor + 1]?.color ?? currentPlayer,
  };
}

/** Source identity only; moves and legality remain entirely server-authoritative. */
export function observationResearchBaseMatches(
  base: ObservationResearchBase,
  frames: readonly GameReviewFrame[],
  currentPlayer: StoneColor,
): boolean {
  return (
    JSON.stringify(frames.slice(0, base.frame.moveNumber + 1).map(observationFrameKey)) ===
      base.prefixKey &&
    (frames[base.frame.moveNumber + 1]?.color ?? currentPlayer) === base.nextColor
  );
}
